// cleancode-ui/src/App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { EnvironmentProvider, useEnvironment } from './contexts/EnvironmentContext';
import Login from './components/Login';
import SignUp from './components/SignUp';
import Dashboard from './components/Dashboard';
import CleanCodeReview from './components/CleanCodeReview';
import ReviewHistory from './components/ReviewHistory';
import Settings from './components/Settings';

// Component to handle GitHub OAuth callback
function GitHubCallback({ onLogin }) {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if this is a GitHub OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    console.log('GitHubCallback: URL params:', urlParams.toString());
    console.log('GitHubCallback: github param:', urlParams.get('github'));
    
    if (urlParams.get('github') === 'connected') {
      console.log('GitHubCallback: GitHub OAuth successful, logging in...');
      // GitHub OAuth was successful, log the user in
      onLogin();
      // Redirect to review page with GitHub parameter so CleanCodeReview can handle it
      console.log('GitHubCallback: Redirecting to /review with github=connected...');
      navigate('/review?github=connected', { replace: true });
    } else {
      console.log('GitHubCallback: Not a GitHub callback, redirecting to login...');
      // Not a GitHub callback, redirect to login
      navigate('/login', { replace: true });
    }
  }, [onLogin, navigate]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      fontSize: '18px',
      color: '#4A90E2'
    }}>
      <div>
        <div style={{ marginBottom: '20px' }}>🔗 Connecting to GitHub...</div>
        <div style={{ fontSize: '14px', color: '#666' }}>Redirecting to your dashboard...</div>
      </div>
    </div>
  );
}

// App component that can access environment context
function AppWithEnvironment() {
  const { apiBaseUrl } = useEnvironment();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on app startup and handle payment success
  useEffect(() => {
    const checkAuthStatus = async () => {
      // Handle payment success auto-login first
      const urlParams = new URLSearchParams(window.location.search);
      const payment = urlParams.get('payment');
      const autoLogin = urlParams.get('auto_login');
      
      if (payment === 'success' && autoLogin === 'true') {
        const token = localStorage.getItem('token');
        if (!token) {
          // Auto-login the user after successful checkout
          const tempToken = 'temp_token_' + Date.now();
          localStorage.setItem('token', tempToken);
          localStorage.setItem('userEmail', 'user@example.com'); // Temporary email
          console.log('App: Auto-login completed with temporary token');
          
          // Clear URL parameters and redirect to dashboard
          const currentDomain = window.location.origin;
          console.log('App: Current domain:', currentDomain);
          console.log('App: Redirecting to:', `${currentDomain}/dashboard`);
          window.history.replaceState({}, '', `${currentDomain}/dashboard`);
          setIsLoggedIn(true);
          setIsLoading(false);
          return;
        }
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        // Verify token is still valid by making a test API call
        try {
          const response = await fetch(`${apiBaseUrl}/billing/trial-status`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (response.ok) {
            console.log('App: Token is valid, user is logged in');
            setIsLoggedIn(true);
          } else {
            console.log('App: Token is invalid, clearing localStorage');
            localStorage.removeItem('token');
            localStorage.removeItem('userEmail');
          }
        } catch (error) {
          console.error('App: Error checking auth status:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('userEmail');
        }
      }
      setIsLoading(false);
    };

    checkAuthStatus();
  }, [apiBaseUrl]);

  const handleLogin = () => {
    console.log('App: handleLogin called, setting isLoggedIn to true');
    setIsLoggedIn(true);
  };

  const handleLogout = async () => {
    console.log('App: handleLogout called, setting isLoggedIn to false');
    
    try {
      // Call logout API endpoint
      await fetch(`${apiBaseUrl}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Error calling logout API:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    setIsLoggedIn(false);
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#4A90E2'
      }}>
        <div>🔄 Loading...</div>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login onLogin={handleLogin} />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/callback" element={<GitHubCallback onLogin={handleLogin} />} />
        <Route path="/review" element={<CleanCodeReview onLogout={handleLogout} />} />
        {isLoggedIn ? (
          <>
            <Route path="/" element={<Dashboard onLogout={handleLogout} />} />
            <Route path="/dashboard" element={<Dashboard onLogout={handleLogout} />} />
            <Route path="/history" element={<ReviewHistory onLogout={handleLogout} />} />
            <Route path="/settings" element={<Settings onLogout={handleLogout} />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        ) : (
          <Route path="*" element={<Navigate to="/login" replace />} />
        )}
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <EnvironmentProvider>
      <AppWithEnvironment />
    </EnvironmentProvider>
  );
}

export default App;